// src/components/ChefMode.jsx  —  ohapotato v2026.10.36
//
// 👨‍🍳 CHEF MODE — Premium feature
// An AI-powered cooking coach that layers professional knowledge onto any recipe.
// Unlocks: technique breakdowns, smart substitutions, wine & drink pairings,
// flavour science notes, plating tips, and a pro difficulty rating.
// Calls /api/chef-mode — Gemini key stays server-side, never in the bundle.

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

// ── Section definitions ───────────────────────────────────────────────────────

const SECTIONS = [
  { key: 'techniques',     icon: '🔪', label: 'Knife & Technique'   },
  { key: 'science',        icon: '🧪', label: 'Flavour Science'      },
  { key: 'substitutions',  icon: '🔄', label: 'Smart Substitutions'  },
  { key: 'plating',        icon: '🍽', label: 'Plating & Presentation'},
  { key: 'pairings',       icon: '🍷', label: 'Drink Pairings'       },
  { key: 'proTips',        icon: '⭐', label: 'Pro Tips'             },
];

// ── Gemini call ───────────────────────────────────────────────────────────────

async function fetchChefInsights(recipe) {
  const res = await fetch('/api/chef-mode', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ recipe }),
  });

  const text = await res.text();
  let data = {};
  try { data = JSON.parse(text); } catch { /* non-JSON */ }

  if (!res.ok) {
    if (res.status === 429) throw new Error('Rate limit reached — please wait a moment and try again.');
    throw new Error(data.error || `Server error ${res.status}`);
  }

  if (!data.insights) throw new Error('No insights returned from server.');
  return data.insights;
}


// ── Pill badge ────────────────────────────────────────────────────────────────

function ProBadge({ level }) {
  const colors = {
    'Home Cook':      { bg: 'var(--bg-pill)',           color: 'var(--text-muted)' },
    'Confident Cook': { bg: 'var(--accent-orange-bg)',  color: 'var(--accent-orange)' },
    'Advanced':       { bg: 'var(--accent-green-bg)',   color: 'var(--accent-green)' },
    'Chef Level':     { bg: '#FEF3C7',                  color: '#B45309' },
  };
  const c = colors[level] || colors['Home Cook'];
  return (
    <span style={{
      fontSize: '11px', fontWeight: '700', letterSpacing: '0.4px',
      padding: '4px 12px', borderRadius: 'var(--radius-pill)',
      fontFamily: 'var(--font-body)',
      background: c.bg, color: c.color,
    }}>
      {level === 'Chef Level' ? '⭐ ' : ''}{level}
    </span>
  );
}

// ── Section tab bar ───────────────────────────────────────────────────────────

function TabBar({ active, onChange }) {
  return (
    <div style={t.bar}>
      {SECTIONS.map(sec => (
        <button key={sec.key} onClick={() => onChange(sec.key)}
          style={{
            ...t.tab,
            ...(active === sec.key ? t.tabActive : {}),
          }}>
          <span>{sec.icon}</span>
          <span style={t.tabLabel}>{sec.label}</span>
        </button>
      ))}
    </div>
  );
}

const t = {
  bar: {
    display: 'flex', overflowX: 'auto', gap: '6px',
    padding: '12px 16px 0', flexShrink: 0,
    scrollbarWidth: 'none',
  },
  tab: {
    display: 'flex', alignItems: 'center', gap: '5px',
    padding: '7px 12px', borderRadius: 'var(--radius-pill)',
    border: '1.5px solid var(--border-base)',
    background: 'var(--bg-pill)', cursor: 'pointer',
    fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: '600',
    color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0,
    transition: 'all var(--transition)',
  },
  tabActive: {
    background: 'var(--accent-gold)', color: '#fff',
    borderColor: 'var(--accent-gold)',
  },
  tabLabel: { fontSize: '11px' },
};

// ── Section renderers ─────────────────────────────────────────────────────────

function TechniqueSection({ data }) {
  return (
    <div style={r.list}>
      {(data || []).map((item, i) => (
        <div key={i} style={r.card}>
          <div style={r.cardTitle}>{item.name}</div>
          <p style={r.cardText}>{item.detail}</p>
        </div>
      ))}
    </div>
  );
}

function ScienceSection({ data }) {
  return (
    <div style={r.list}>
      {(data || []).map((item, i) => (
        <div key={i} style={{ ...r.card, borderLeft: '3px solid var(--accent-green)' }}>
          <div style={{ ...r.cardTitle, color: 'var(--accent-green)' }}>🧪 {item.title}</div>
          <p style={r.cardText}>{item.detail}</p>
        </div>
      ))}
    </div>
  );
}

function SubstitutionsSection({ data }) {
  return (
    <div style={r.list}>
      {(data || []).map((item, i) => (
        <div key={i} style={r.subRow}>
          <div style={r.subLeft}>
            <span style={r.subOriginal}>{item.original}</span>
            <span style={r.subArrow}>→</span>
            <span style={r.subSwap}>{item.swap}</span>
          </div>
          <p style={r.subNote}>{item.note}</p>
        </div>
      ))}
    </div>
  );
}

function PlatingSection({ data }) {
  if (!data) return null;
  return (
    <div style={r.list}>
      <div style={{ ...r.card, background: 'var(--accent-orange-bg)', border: '1px solid var(--accent-orange)33' }}>
        <div style={r.cardTitle}>Style: {data.style}</div>
      </div>
      {(data.tips || []).map((tip, i) => (
        <div key={i} style={r.tipRow}>
          <span style={r.tipNum}>{i + 1}</span>
          <p style={r.tipText}>{tip}</p>
        </div>
      ))}
    </div>
  );
}

function PairingsSection({ data }) {
  const typeColor = {
    'Wine':          { bg:'#FDF2F8', color:'#9D174D' },
    'Beer':          { bg:'#FFFBEB', color:'#92400E' },
    'Cocktail':      { bg:'#EFF6FF', color:'#1E40AF' },
    'Non-alcoholic': { bg:'var(--accent-green-bg)', color:'var(--accent-green)' },
  };
  return (
    <div style={r.list}>
      {(data || []).map((item, i) => {
        const c = typeColor[item.type] || typeColor['Non-alcoholic'];
        return (
          <div key={i} style={r.pairingRow}>
            <span style={{ ...r.pairingType, background: c.bg, color: c.color }}>{item.type}</span>
            <div style={r.pairingInfo}>
              <span style={r.pairingSuggestion}>{item.suggestion}</span>
              <span style={r.pairingReason}>{item.reason}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProTipsSection({ data }) {
  return (
    <div style={r.list}>
      {(data || []).map((tip, i) => (
        <div key={i} style={r.tipRow}>
          <span style={{ ...r.tipNum, background: 'var(--accent-gold)', color: '#fff' }}>⭐</span>
          <p style={r.tipText}>{tip}</p>
        </div>
      ))}
    </div>
  );
}

// shared styles for renderers
const r = {
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  card: {
    background: 'var(--bg-card)', border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)', padding: '12px 14px',
  },
  cardTitle: {
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '700',
    color: 'var(--text-primary)', marginBottom: '5px',
  },
  cardText: {
    fontFamily: 'var(--font-body)', fontSize: '13px', lineHeight: '1.6',
    color: 'var(--text-secondary)', margin: 0,
  },
  subRow: {
    background: 'var(--bg-card)', border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)', padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: '5px',
  },
  subLeft: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  subOriginal: {
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '700',
    color: 'var(--text-primary)',
    textDecoration: 'line-through', textDecorationColor: 'var(--accent-orange)',
  },
  subArrow: { color: 'var(--text-muted)', fontWeight: '700' },
  subSwap: {
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '700',
    color: 'var(--accent-green)',
  },
  subNote: {
    fontFamily: 'var(--font-body)', fontSize: '12.5px', lineHeight: '1.55',
    color: 'var(--text-muted)', margin: 0,
  },
  tipRow: {
    display: 'flex', gap: '12px', alignItems: 'flex-start',
    background: 'var(--bg-card)', border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)', padding: '12px 14px',
  },
  tipNum: {
    width: '22px', height: '22px', borderRadius: '50%',
    background: 'var(--accent-orange)', color: '#fff',
    fontSize: '10px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  tipText: {
    fontFamily: 'var(--font-body)', fontSize: '13px', lineHeight: '1.6',
    color: 'var(--text-secondary)', margin: 0, flex: 1,
  },
  pairingRow: {
    display: 'flex', gap: '12px', alignItems: 'flex-start',
    background: 'var(--bg-card)', border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)', padding: '12px 14px',
  },
  pairingType: {
    fontSize: '10px', fontWeight: '700', padding: '3px 9px',
    borderRadius: 'var(--radius-pill)', whiteSpace: 'nowrap', flexShrink: 0,
    fontFamily: 'var(--font-body)',
  },
  pairingInfo: { display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 },
  pairingSuggestion: {
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '700',
    color: 'var(--text-primary)',
  },
  pairingReason: {
    fontFamily: 'var(--font-body)', fontSize: '12px', lineHeight: '1.5',
    color: 'var(--text-muted)',
  },
};

// ── Loading skeleton ──────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[1, 2, 3].map(i => (
        <div key={i} style={{
          height: '80px', borderRadius: 'var(--radius-md)',
          background: 'var(--bg-pill)',
          animation: 'shimmer 1.4s infinite linear',
          backgroundImage: 'linear-gradient(90deg, var(--bg-pill) 0%, var(--border-base) 50%, var(--bg-pill) 100%)',
          backgroundSize: '200% 100%',
        }} />
      ))}
    </div>
  );
}

// ── Main ChefMode component ───────────────────────────────────────────────────

export default function ChefMode({ recipe, onClose }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [activeTab, setActiveTab] = useState('techniques');
  const bodyRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    setLoading(true); setError(null);
    fetchChefInsights(recipe)
      .then(data => { setInsights(data); setLoading(false); })
      .catch(err  => { setError(err.message); setLoading(false); });
  }, [recipe]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const active = SECTIONS.find(s => s.key === activeTab);

  function renderSection() {
    if (!insights) return null;
    switch (activeTab) {
      case 'techniques':    return <TechniqueSection    data={insights.techniques}    />;
      case 'science':       return <ScienceSection      data={insights.science}       />;
      case 'substitutions': return <SubstitutionsSection data={insights.substitutions} />;
      case 'plating':       return <PlatingSection      data={insights.plating}       />;
      case 'pairings':      return <PairingsSection     data={insights.pairings}      />;
      case 'proTips':       return <ProTipsSection      data={insights.proTips}       />;
      default: return null;
    }
  }

  return ReactDOM.createPortal(
    <div style={s.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label="Chef Mode">
      <div style={s.dialog} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.headerLeft}>
            <div style={s.goldBadge}>👨‍🍳 Chef Mode</div>
            <div style={s.headerRecipe}>
              <span style={s.recipeEmoji}>{recipe.emoji}</span>
              <span style={s.recipeTitle}>{recipe.title}</span>
            </div>
          </div>
          <div style={s.headerRight}>
            {insights && <ProBadge level={insights.proLevel} />}
            <button style={s.closeBtn} onClick={onClose} aria-label="Close Chef Mode">✕</button>
          </div>
        </div>

        {/* Pro level note */}
        {insights?.proLevelNote && !loading && (
          <div style={s.levelNote}>
            <span style={s.levelNoteText}>{insights.proLevelNote}</span>
          </div>
        )}

        {/* Section tab bar */}
        <TabBar active={activeTab} onChange={setActiveTab} />

        {/* Section label */}
        <div style={s.sectionLabel}>
          <span>{active?.icon} {active?.label}</span>
        </div>

        {/* Body */}
        <div ref={bodyRef} style={s.body}>
          {loading && <Skeleton />}
          {error && (
            <div style={s.errorBox}>
              <div style={s.errorTitle}>Couldn't load Chef Mode</div>
              <p style={s.errorText}>{error}</p>
              <p style={s.errorHint}>Chef Mode is unavailable — please try again later or contact support.</p>
            </div>
          )}
          {!loading && !error && renderSection()}
        </div>

        {/* Footer */}
        <div style={s.footer}>
          <div style={s.footerLeft}>
            <span style={s.premiumLabel}>✦ Premium feature</span>
          </div>
          <button style={s.closeFooterBtn} onClick={onClose}>Done</button>
        </div>

      </div>
    </div>
  , document.body);
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 500,
    background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
    animation: 'fadeUp 0.15s ease',
  },
  dialog: {
    width: '100%', maxWidth: '580px', maxHeight: '92vh',
    background: 'var(--bg-surface)',
    borderRadius: '20px',
    boxShadow: '0 32px 80px rgba(0,0,0,0.4)',
    border: '1.5px solid var(--accent-gold)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    animation: 'dialogPop 0.25s cubic-bezier(0.34,1.56,0.64,1)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px 12px', borderBottom: '1px solid var(--border-base)',
    flexShrink: 0, gap: '12px',
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, flex: 1 },
  goldBadge: {
    fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px',
    color: 'var(--accent-gold)', fontFamily: 'var(--font-body)',
  },
  headerRecipe: { display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0 },
  recipeEmoji: { fontSize: '20px', flexShrink: 0 },
  recipeTitle: {
    fontFamily: 'var(--font-display)', fontSize: '15px', fontWeight: '700',
    color: 'var(--text-primary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  closeBtn: {
    width: '30px', height: '30px', borderRadius: '50%',
    border: 'none', background: 'var(--bg-pill)', fontSize: '13px',
    color: 'var(--text-muted)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  levelNote: {
    padding: '8px 16px', background: 'var(--accent-gold)15',
    borderBottom: '1px solid var(--accent-gold)33', flexShrink: 0,
  },
  levelNoteText: {
    fontFamily: 'var(--font-body)', fontSize: '12.5px',
    color: 'var(--text-secondary)', lineHeight: '1.5',
    fontStyle: 'italic',
  },
  sectionLabel: {
    padding: '10px 16px 4px',
    fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: '700',
    letterSpacing: '1px', textTransform: 'uppercase',
    color: 'var(--text-muted)', flexShrink: 0,
  },
  body: {
    flex: 1, overflowY: 'auto',
    padding: '8px 16px 16px',
  },
  errorBox: {
    background: 'var(--bg-card)', border: '1px solid var(--border-base)',
    borderRadius: 'var(--radius-md)', padding: '20px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  errorTitle: {
    fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: '700',
    color: 'var(--text-primary)',
  },
  errorText: {
    fontFamily: 'var(--font-body)', fontSize: '13px',
    color: 'var(--accent-orange)', margin: 0, lineHeight: '1.5',
  },
  errorHint: {
    fontFamily: 'var(--font-body)', fontSize: '12px',
    color: 'var(--text-muted)', margin: 0,
  },
  footer: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 16px', borderTop: '1px solid var(--border-base)', flexShrink: 0,
  },
  footerLeft: { display: 'flex', alignItems: 'center' },
  premiumLabel: {
    fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: '600',
    color: 'var(--accent-gold)',
  },
  closeFooterBtn: {
    padding: '8px 24px', border: 'none', borderRadius: 'var(--radius-md)',
    background: 'var(--accent-gold)', color: '#fff',
    fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: '700',
    cursor: 'pointer',
  },
};
